import { G } from '@mobily/ts-belt';
import { SegmentedButtons, Typography } from '@scale/scaleui';
import {
  Attachment,
  AttachmentSource,
  ModelInputFileAttachmentType,
} from '@scale/shared/types/chat';
import _ from 'lodash';
import { useContext, useState, useMemo } from 'react';
import { Alert } from 'react-scale/components/scalers/tasks/ThreadedInteraction/TempComponents';
import apiFetch from 'react-scale/lib/api-fetch';
import { toast } from 'react-toastify';
import { ChatTaskContext } from '../contexts/ChatTaskContext';
import FileUpload from './FileUpload';
import MMImageServiceAttachmentSelector from './MMImageServiceAttachmentSelector';
import ChatImageUrlAttachmentTool from './ChatImageUrlAttachmentTool';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useMutation } from 'react-query';
import { MultimodalImageServiceHook } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useMultimodalImageService';
import {
  MultimodalAssetDto,
  formatMaybeErrorForLogging,
  ContentUnderstandingUploadResponseBody,
  ImageValidationError,
  createAwsTranscriptionService,
  createWhisperTranscriptionService,
} from '@scale/mm-shared';
import { checkImageOriginality } from '@scale/frontend-lib/dashboard-api/multimodal';
import useCDSMultipartUpload from 'react-scale/components/scalers/tasks/textcollection/hooks/useCDSMultipartUpload';
import { processAudioAttachment } from 'react-scale/lib/chat-api/multimodal';
import retry from '@scale/shared/utils/retry';
import logger from 'react-scale/lib/logging';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { getFileType } from 'react-scale/components/scalers/tasks/textcollection/fileUploadUtils';
import { decodeAudioBuffer } from '@scale/frontend-lib/audio/decodeAudio';

const OWID_DYNAMIC_GRAPH_URL_PREFIX = 'ourworldindata.org/grapher/';

type EditAttachmentsProps = {
  attachments: Attachment[];
  setAttachments: (attachment: Attachment[]) => void;
  supportedMimeTypesByInputType: Record<ModelInputFileAttachmentType, string[]>;
  multimodalImageServiceHook?: MultimodalImageServiceHook;
  shouldMirrorImageUrlsToTempBucket?: boolean;
  enableAttachmentsOverride?: boolean;
  enableImageUrlsOverride?: boolean;
  enableMmImageServiceOverride?: boolean;
  updateImageMetadata?: (url: string, metadata: MultimodalAssetDto) => void;
  fileSizeLimitInBytes?: number;
  multipleFiles?: boolean;
  beforeFileProcessing?: () => void;
};

/**
 * This component should only render when at least one of these props are true
 * 1. enableImageUrls
 * 2. enableAttachments
 * 3. enableMmImageService
 */
const EditAttachments = ({
  setAttachments,
  attachments,
  supportedMimeTypesByInputType,
  multimodalImageServiceHook,
  shouldMirrorImageUrlsToTempBucket,
  enableAttachmentsOverride,
  enableImageUrlsOverride,
  enableMmImageServiceOverride,
  updateImageMetadata,
  fileSizeLimitInBytes,
  multipleFiles,
  beforeFileProcessing,
}: EditAttachmentsProps) => {
  const totalFileSizeLimitInBytes = fileSizeLimitInBytes ?? 5000000; // default to 5MB

  const { projectId, assignmentId, workerId } = useContext(ChatTaskContext);
  const config = useChatStoreExtract(state => state.config);
  const enableImageUrls = _.isNil(enableImageUrlsOverride)
    ? config.useImageUrls
    : enableImageUrlsOverride;
  const enableAttachments = _.isNil(enableAttachmentsOverride)
    ? config.useAttachments
    : enableAttachmentsOverride;
  const enableMmImageService = _.isNil(enableMmImageServiceOverride)
    ? config.enableMmImageService
    : enableMmImageServiceOverride;

  const supportedMimeTypesByInputTypeWithImageUpdated = useMemo(() => {
    const allowedImageMimeTypes = config.imageValidatorSettings?.allowedExtensions;
    // treat empty array as no restriction for images
    if (!allowedImageMimeTypes || allowedImageMimeTypes.length === 0) {
      return supportedMimeTypesByInputType;
    }
    return {
      ...supportedMimeTypesByInputType,
      [ModelInputFileAttachmentType.Image]: allowedImageMimeTypes,
    };
  }, [supportedMimeTypesByInputType, config.imageValidatorSettings]);

  const enabledAttachmentSources = [
    enableMmImageService ? AttachmentSource.MM_IMAGE_SERVICE : null,
    enableImageUrls ? AttachmentSource.URL : null,
    enableAttachments ? AttachmentSource.FILE_UPLOAD : null,
  ].filter(G.isNotNullable);

  const cdsMultipartUpload = useCDSMultipartUpload(assignmentId, workerId);

  /**
   * attachmentSource will be undefined if enabledAttachmentSources.length === 0
   */
  const [attachmentSource, setAttachmentSource] = useState<AttachmentSource>(
    enabledAttachmentSources[0],
  );

  const shouldUseCDSMultipartUploadNonAudio = useUnleashFeatureFlag(
    'chat-enable-cds-multipart-upload-prompt',
    { projectId },
    false,
  );

  const shouldUseWhisperTranscription = useUnleashFeatureFlag(
    'chat-v1-use-whisper-transcription',
    { projectId },
    false,
  );

  /**
   * this mutation is for non-file uploads
   */
  const { mutateAsync: addAttachmentAsync, isLoading: isAddingAttachment } = useMutation(
    async (attachment: Attachment): Promise<void> => {
      // if feature flag is enabled, mirror the image at the url to a temp Scale-hosted bucket and add both mirrored url and original url to the attachment object
      if (attachment.url && shouldMirrorImageUrlsToTempBucket) {
        const mirroredUrl: string = await apiFetch(
          null,
          `internal/genai/mirrorToTempBucket?url=${encodeURIComponent(
            attachment.url,
          )}&projectId=${projectId}&assignmentId=${assignmentId}`,
          {
            method: 'GET',
          },
        );
        attachment.originalUrl = attachment.url;
        attachment.url = mirroredUrl;
      }

      setAttachments([...attachments, attachment]);
      if (multimodalImageServiceHook) {
        multimodalImageServiceHook.setTimeToSearch(
          Date.now() - multimodalImageServiceHook.searchStartTime,
        );
      }
    },
  );

  const onFiles = async (files: File[]) => {
    if (beforeFileProcessing) {
      beforeFileProcessing();
    }
    try {
      await toast.promise(
        async () => {
          const newAttachments = _.cloneDeep(attachments);
          // TODO: Use pMap to speed up the upload
          for (const file of files) {
            const fileType = getFileType(file);
            if (!fileType) {
              toast.error('Unsupported file type', { position: 'bottom-right' });
              throw new Error();
            }
            const formData = new FormData();
            formData.append('asset', file);
            formData.append('assignmentId', assignmentId ?? '');
            const key = projectId;
            try {
              // we do audio format conversion inside of the CDS multipart upload
              // todo: separation of concerns
              const uploadResult = await (async (): Promise<{
                url: string;
                mimeType: string;
                name: string;
              }> => {
                if (fileType === ModelInputFileAttachmentType.Audio) {
                  const { cdsUrl } = await cdsMultipartUpload.upload({
                    file,
                    fileType: config.audio?.format,
                  });

                  try {
                    await retry(
                      () =>
                        processAudioAttachment({
                          audioUrl: cdsUrl,
                          audioFormat: config.audio?.format,
                          assignmentId,
                          workerId,
                          userGeneratedAudio: true,
                          transcriptionService: shouldUseWhisperTranscription
                            ? createWhisperTranscriptionService()
                            : createAwsTranscriptionService(),
                        }),
                      { limit: 3 },
                    );
                  } catch (error) {
                    logger.error(`EditAttachments: Failed to start processing audio attachment`, {
                      projectId,
                      assignmentId,
                      workerId,
                      error: formatMaybeErrorForLogging(error),
                    });
                  }

                  return {
                    url: cdsUrl,
                    mimeType: config.audio?.format ? `audio/${config.audio.format}` : file.type,
                    name: config.audio?.format ? `${file.name}.${config.audio.format}` : file.name,
                  };
                }

                let url: string;
                if (shouldUseCDSMultipartUploadNonAudio) {
                  const { cdsUrl: _cdsUrl } = await cdsMultipartUpload.upload({
                    file,
                  });
                  url = _cdsUrl;
                } else {
                  const uploadResponse = await apiFetch<ContentUnderstandingUploadResponseBody>(
                    null,
                    `internal/contentUnderstanding/fileUpload/${key}`,
                    {
                      method: 'POST',
                      body: formData,
                    },
                  );
                  if (uploadResponse.type === 'error') {
                    uploadResponse.errors.forEach((message: string) => {
                      toast.error(message, { position: 'bottom-right' });
                    });
                    // this is not ideal to use for control flow but it requires refactoring the toast
                    // if we return without error, the toast shows success even though we hit an edge case
                    throw new ImageValidationError();
                  }
                  url = uploadResponse.s3Url;
                }

                return { url, mimeType: file.type, name: file.name };
              })();

              let duration: number | undefined = undefined;
              if (fileType === ModelInputFileAttachmentType.Audio) {
                const audioBuffer = await decodeAudioBuffer(await file.arrayBuffer());
                ({ duration } = audioBuffer ?? {});
              }

              newAttachments.push({
                name: uploadResult.name,
                url: uploadResult.url,
                mimeType: uploadResult.mimeType,
                fileType,
                fileSizeInBytes: file.size,
                imageSubmissionMethod: AttachmentSource.FILE_UPLOAD,
                duration,
              });
            } catch (e) {
              if (!(e instanceof ImageValidationError)) {
                toast.error('Error uploading file', { position: 'bottom-right' });
              }
              throw new Error();
            }
            if (multimodalImageServiceHook) {
              multimodalImageServiceHook.setTimeToSearch(
                Date.now() - multimodalImageServiceHook.searchStartTime,
              );
            }
          }
          setAttachments(newAttachments);
        },
        {
          pending: 'Uploading files',
          success: 'Files uploaded successfully',
        },
        { position: 'bottom-right' },
      );
    } catch {
      // ignore
    }
  };

  const handleSubmitAttachment = (attachment: Attachment) => {
    toast.promise(
      addAttachmentAsync(attachment),
      {
        pending: 'Attaching...',
        success: {
          render: 'Attached!',
          delay: 100, // Add a small delay to ensure the toast properly transitions
        },
        error: {
          render: 'Error attaching',
          delay: 100,
        },
      },
      { position: 'bottom-right', closeButton: true },
    );
  };

  // programatically convert OWID dynamic graph urls to static png urls
  const getStaticPngUrlForOWIDDynamicGraph = (url: string) => {
    const { pathname } = new URL(url);
    if (pathname.startsWith('/grapher/thumbnail/') || pathname.substring(-4) === '.png') {
      return url;
    }

    let staticUrl = url.replace('grapher/', 'grapher/thumbnail/');
    if (staticUrl.includes('?')) {
      staticUrl = staticUrl.replace('?', '.png?');
    } else {
      staticUrl = `${staticUrl}.png`;
    }
    return staticUrl;
  };

  const transformUrlBeforeSubmit = (url: string): string => {
    if (url.includes(OWID_DYNAMIC_GRAPH_URL_PREFIX)) {
      return getStaticPngUrlForOWIDDynamicGraph(url);
    }
    return url;
  };

  return (
    <div className="flex flex-col gap-4" style={{ minWidth: '500px' }}>
      {enabledAttachmentSources.length === 0 ? (
        <Alert variant="danger">
          <Typography.H4>Attachments have not been enabled</Typography.H4>
        </Alert>
      ) : (
        <>
          <SegmentedButtons
            buttons={enabledAttachmentSources.map(source => ({ id: source, label: source }))}
            selected={attachmentSource}
            onSelect={source => setAttachmentSource(source as AttachmentSource)}
          />
          {attachmentSource === AttachmentSource.FILE_UPLOAD && (
            <FileUpload
              allowedMimeTypesByInputType={supportedMimeTypesByInputTypeWithImageUpdated}
              onFiles={onFiles}
              setError={error => error && toast.error(error, { position: 'bottom-right' })}
              totalFileSizeLimitInBytes={totalFileSizeLimitInBytes}
              checkImageOriginality={checkImageOriginality}
              projectId={projectId}
              multipleFiles={multipleFiles}
            />
          )}
          {attachmentSource === AttachmentSource.URL && (
            <ChatImageUrlAttachmentTool
              onSubmit={handleSubmitAttachment}
              disabled={isAddingAttachment}
              transformUrl={transformUrlBeforeSubmit}
            />
          )}
          {attachmentSource === AttachmentSource.MM_IMAGE_SERVICE &&
            projectId &&
            multimodalImageServiceHook && (
              <MMImageServiceAttachmentSelector
                onSubmit={handleSubmitAttachment}
                disabled={isAddingAttachment}
                projectId={projectId}
                assignmentId={assignmentId}
                multimodalImageServiceHook={multimodalImageServiceHook}
                updateImageMetadata={updateImageMetadata}
              />
            )}
        </>
      )}
    </div>
  );
};

export default EditAttachments;
